"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/ChatOnboarding/Steps/LoadingStep/LoadingStep";

import { Icon } from "@ospweb/sdk-ui";
import { LoadingRipples } from "./LoadingRipples";
import { LoadingTitleDots } from "./LoadingTitleDots";
import {
  LOADING_STEP_ASSET_SIZE,
  LOADING_STEP_DEFAULT_BACKGROUND_SRC,
  LOADING_STEP_FIGMA,
  LOADING_STEP_LAYOUT,
} from "./loadingStep.contract";
import { cn } from "utils";
import { useIsPc } from "@/src/hooks";
import { AppImage } from "@/src/components/NextImage";

const I18n = I18nUtil.createScoped(Translations);

export interface LoadingStepProps {
  className?: string;
  playing?: boolean;
  /** H5 top-left back. */
  onBack?: () => void;
  /** PC top-right close (Figma `88328:5688`). */
  onClose?: () => void;
  /** `page` = route/full-bleed; `modal` = inside EdgenModal (PC/H5 shell). */
  layout?: "page" | "modal";
  /** 背景图 URL；默认 {@link LOADING_STEP_DEFAULT_BACKGROUND_SRC} */
  backgroundSrc?: string;
  /** 涟漪在容器内水平 + 垂直居中（聚合 480×674 / H5 全屏等非 393×852 帧） */
  rippleCentered?: boolean;
  /** 涟漪圆环峰值透明度；未传时默认 85%。 */
  ringPeakOpacity?: number;
}

const LoadingHeroIcon = () => (
  <div
    className="relative h-[120px] w-14 shrink-0"
    data-testid="onboarding-loading-hero-icon"
    data-figma-node-id={LOADING_STEP_FIGMA.heroIcon}
  >
    <div
      className="absolute left-0 top-0 size-14 rounded-full bg-content_aura_lv3_primary"
      data-testid="onboarding-loading-icon-head"
      data-figma-node-id={LOADING_STEP_FIGMA.iconHead}
    />
    <div
      className="absolute left-0.5 top-[68px] size-[52px] rounded-[2px] bg-content_aura_lv3_primary"
      data-testid="onboarding-loading-icon-body"
      data-figma-node-id={LOADING_STEP_FIGMA.iconBody}
    />
  </div>
);

export const LoadingStep: React.FC<LoadingStepProps> = ({
  className,
  playing = true,
  onBack,
  onClose,
  layout = "page",
  backgroundSrc = LOADING_STEP_DEFAULT_BACKGROUND_SRC,
  rippleCentered = false,
  ringPeakOpacity,
}) => {
  const [isPc] = useIsPc();
  /** 393px 稿坐标；宽 modal（如 DiagnosisUnlock 480px）需水平居中 */
  const isModalPcCentered = layout === "modal" && isPc;
  const rippleCenterHorizontally = isModalPcCentered || rippleCentered;

  return (
    <div
      className={cn(
        "relative w-full overflow-hidden",
        layout === "modal" ? "h-full min-h-0" : "h-[852px] min-h-dvh",
        className,
      )}
      data-testid="onboarding-loading-step"
      data-figma-node-id={LOADING_STEP_FIGMA.page}
    >
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden"
        data-testid="onboarding-loading-background"
        data-figma-node-id={LOADING_STEP_FIGMA.background}
      >
        <AppImage
          src={backgroundSrc}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          width={LOADING_STEP_ASSET_SIZE.background.width}
          height={LOADING_STEP_ASSET_SIZE.background.height}
          loading="eager"
          decoding="async"
        />
      </div>
      <LoadingRipples
        playing={playing}
        centerHorizontally={rippleCenterHorizontally}
        centerVertically={rippleCentered}
        ringPeakOpacity={ringPeakOpacity}
      />
      {!isPc ? (
        <div
          className={cn(
            "relative z-10 flex flex-col",
            layout === "modal" && "pt-safe",
          )}
          data-testid="onboarding-loading-top-block"
          data-figma-node-id={LOADING_STEP_FIGMA.topBlock}
        >
          <div
            className="flex shrink-0 items-center px-4 py-2"
            data-testid="onboarding-loading-top-nav"
            data-figma-node-id={LOADING_STEP_FIGMA.topNav}
          >
            <button
              type="button"
              onClick={onBack}
              className="flex size-10 items-center justify-center rounded-[20px] bg-background_solid_neutral_primary"
              data-testid="onboarding-loading-back"
              data-figma-node-id={LOADING_STEP_FIGMA.backButton}
              aria-label="Back"
            >
              <Icon
                name="left-arrow-alt"
                size={20}
                className="text-content_primary"
              />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={onClose ?? onBack}
          className="absolute right-4 top-4 z-20 flex size-10 items-center justify-center rounded-full"
          data-testid="onboarding-loading-close"
          data-figma-node-id={LOADING_STEP_FIGMA.closeButton}
          aria-label="Close"
        >
          <Icon
            name="close"
            size={20}
            className="text-content_inverse_primary"
          />
        </button>
      )}
      <div
        className={cn(
          "absolute z-10 flex w-[345px] flex-col",
          isModalPcCentered && "left-1/2 -translate-x-1/2",
        )}
        style={{
          left: isModalPcCentered ? undefined : LOADING_STEP_LAYOUT.contentLeft,
          top: LOADING_STEP_LAYOUT.contentTop,
        }}
        data-testid="onboarding-loading-content-frame"
        data-figma-node-id={LOADING_STEP_FIGMA.contentFrame}
      >
        <div
          className="flex w-full flex-col items-center gap-16"
          data-testid="onboarding-loading-copy-block"
          data-figma-node-id={LOADING_STEP_FIGMA.contentBlock}
        >
          <LoadingHeroIcon />

          <div
            className="flex w-full flex-col items-start"
            data-testid="onboarding-loading-title-block"
            data-figma-node-id={LOADING_STEP_FIGMA.titleBlock}
          >
            <p
              className="title_xxl w-full text-center text-content_inverse_primary"
              data-testid="onboarding-loading-title"
              data-figma-node-id={LOADING_STEP_FIGMA.title}
              aria-live="polite"
            >
              <span data-testid="onboarding-loading-title-text">
                {I18n.t("I'm cross-checking your portfolio. About 20 seconds.")}
              </span>
              {playing ? <LoadingTitleDots /> : null}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
